import { useOrganization } from '@clerk/nextjs';
import { useCallback, useMemo } from 'react';
import { formatCampaignLabelText } from '@/code/frontend/components/campaign/CampaignLabel';
import { CommandGroup } from '@/code/frontend/components/ui/command';
import { useDebounce } from '@/code/frontend/hooks/useDebounce';
import { LinkRouter } from '@/code/lib/routes';
import { fullName } from '@/code/lib/utils/initials';
import { RouterOutput, trpc } from '@/code/lib/utils/trpc';
import {
  CmdKCampaignItem,
  CmdKDialog,
  CmdKLeadItem,
  CmdKPageItem,
} from './components';
import { useCmdKNavigation, useRegularUserPages } from './hooks';
import type { CmdKDialogProps } from './types';

/**
 * Regular user command palette with pages, campaigns, and lead search.
 */
export function RegularCmdK({
  open,
  onOpenChange,
  dbUser,
  isSignedIn,
}: CmdKDialogProps & {
  dbUser: { id: string } | null;
  isSignedIn: boolean;
}) {
  const { query, setQuery, navigate, handleOpenChange, trackMetaKey } =
    useCmdKNavigation({ onOpenChange });
  const debouncedQuery = useDebounce(query, 300);
  const pages = useRegularUserPages();
  const { data: currentUser } = trpc.user.get.useQuery(
    { noNestedObjects: true },
    { enabled: open && Boolean(dbUser) },
  );
  // Deep-link to the org messaging inbox uses the active org's slug, not memberships[0].
  const { organization } = useOrganization();
  const orgSlug = organization?.slug;
  const { data: showInbox } =
    trpc.featureFlags.getIsFlagEnabledForUser.useQuery(
      'SHOULD_SHOW_ORG_MESSAGING_INBOX',
      { enabled: open && isSignedIn },
    );

  const { data: campaigns } = trpc.campaign.getAllActiveCampaigns.useQuery(
    undefined,
    { enabled: open && Boolean(dbUser) },
  );

  const { data: leads, isLoading: leadsLoading } =
    trpc.leads.searchActive.useQuery(
      { query: debouncedQuery, limit: 10 },
      { enabled: open && Boolean(dbUser) && debouncedQuery.trim().length > 0 },
    );

  const filteredPages = useMemo(() => {
    if (!query.trim()) return pages;
    const lowerQuery = query.toLowerCase();
    return pages.filter(
      (page) =>
        page.label.toLowerCase().includes(lowerQuery) ||
        page.keywords?.some((kw) => kw.toLowerCase().includes(lowerQuery)),
    );
  }, [query, pages]);

  const filteredCampaigns = useMemo(() => {
    if (!campaigns) return [];
    if (!query.trim()) return campaigns.slice(0, 5);
    const lowerQuery = query.toLowerCase();
    return campaigns.filter((campaign) => {
      const ownerName = fullName(campaign.user ?? {});
      return formatCampaignLabelText({
        name: campaign.campaignName,
        ownerName,
      })
        .toLowerCase()
        .includes(lowerQuery);
    });
  }, [campaigns, query]);

  const handleLeadSelect = useCallback(
    (lead: RouterOutput['leads']['searchActive'][number]) => {
      if (!currentUser) return;
      // Prefer the org messaging inbox when enabled; inbox resolves the `lead:<id>` sentinel.
      if (showInbox && orgSlug) {
        navigate(
          LinkRouter.routeForOrganizationInbox(orgSlug, {
            c: `lead:${lead.id}`,
          }),
        );
        return;
      }
      const route =
        lead.userId === currentUser.id
          ? LinkRouter.routeForDashboard(lead.campaign.id, lead.id)
          : LinkRouter.routeForOtherDashboard(
              lead.userId,
              lead.campaign.id,
              lead.id,
            );
      navigate(route);
    },
    [navigate, currentUser, showInbox, orgSlug],
  );

  const hasNoResults =
    filteredPages.length === 0 &&
    filteredCampaigns.length === 0 &&
    (!leads || leads.length === 0) &&
    query.trim().length > 0 &&
    !leadsLoading;

  return (
    <CmdKDialog
      open={open}
      onOpenChange={handleOpenChange}
      placeholder="Search pages, campaigns, or leads..."
      query={query}
      onQueryChange={setQuery}
      onKeyDown={trackMetaKey}
      onKeyUp={trackMetaKey}
      onPointerDown={trackMetaKey}
      isLoading={leadsLoading && query.trim().length > 0}
      emptyMessage={hasNoResults ? 'No results found.' : undefined}
    >
      {filteredPages.length > 0 && (
        <CommandGroup heading="Pages">
          {filteredPages.map((page) => (
            <CmdKPageItem key={page.href} page={page} onSelect={navigate} />
          ))}
        </CommandGroup>
      )}

      {filteredCampaigns.length > 0 && (
        <CommandGroup heading="Campaigns">
          {filteredCampaigns.map((campaign) => (
            <CmdKCampaignItem
              key={campaign.id}
              id={campaign.id}
              name={campaign.campaignName}
              ownerName={fullName(campaign.user ?? {})}
              onSelect={() =>
                navigate(LinkRouter.routeForRootCampaign(campaign.id))
              }
            />
          ))}
        </CommandGroup>
      )}

      {leads && leads.length > 0 && (
        <CommandGroup heading="Leads">
          {leads.map((lead) => (
            <CmdKLeadItem
              key={lead.id}
              lead={lead}
              onSelect={handleLeadSelect}
            />
          ))}
        </CommandGroup>
      )}
    </CmdKDialog>
  );
}
